import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Select from "react-select";
import Swal from "sweetalert2";

const RegistrarCentros = ({ actualizarCentros }) => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [codCentro, setCodCentro] = useState("");
    const [tiposedeCentro, setTiposedeCentro] = useState("");
    const [nombreanteriorCentro, setNombreanteriorCentro] = useState("");
    const [nombreCentro, setNombreCentro] = useState("");
    const [nombreAgrupacionCentro, setNombreAgrupacionCentro] = useState("");
    const [municipioCentro, setMunicipioCentro] = useState("");
    const [direccionCentro, setDireccionCentro] = useState("");
    const [codRegional, setCodRegional] = useState("");

    const [regionales, setRegionales] = useState([]);
    const [selectedRegional, setSelectedRegional] = useState(null);

    const [loading, setLoading] = useState(false);
    const isEdit = useMemo(() => Boolean(id), [id]);

    const cleanData = () => {
        setCodCentro("");
        setTiposedeCentro("");
        setNombreanteriorCentro("");
        setNombreCentro("");
        setNombreAgrupacionCentro("");
        setMunicipioCentro("");
        setDireccionCentro("");
        setCodRegional("");
        setSelectedRegional(null);
    };

    const obtenerRegionales = useCallback(async () => {
        try {
            const response = await axios.get("/api/regionales/listall");
            if (response.data.ok) {
                const ordenadas = (response.data.regionales || []).sort((a, b) =>
                    (a?.nombreRegional || "").localeCompare(b?.nombreRegional || "")
                );
                setRegionales(ordenadas);
            } else {
                Swal.fire("Error", response.data.message || "No se pudo cargar regionales", "error");
                setRegionales([]);
            }
        } catch (error) {
            console.error("Error de red:", error?.message);
            Swal.fire("Error", "No se pudo cargar regionales", "error");
            setRegionales([]);
        }
    }, []);

    const regionalOptions = useMemo(
        () =>
            regionales.map((regional) => ({
                value: regional.nombreRegional,
                label: regional.nombreRegional,
                codRegional: regional.codRegional,
                id: regional._id,
            })),
        [regionales]
    );

    const getData = useCallback(async () => {
        try {
            const response = await axios.get(`/api/centrosdeformacion/listid/${id}`);
            if (response.data.ok) {
                const centro = response.data.centro;

                setCodCentro(centro.codCentro ?? "");
                setTiposedeCentro(centro.tiposedeCentro ?? "");
                setNombreanteriorCentro(centro.nombreanteriorCentro ?? "");
                setNombreCentro(centro.nombreCentro ?? "");
                setNombreAgrupacionCentro(centro.nombreAgrupacionCentro ?? "");
                setMunicipioCentro(centro.municipioCentro ?? "");
                setDireccionCentro(centro.direccionCentro ?? "");
                setCodRegional(centro.codRegional ?? "");

                // ✅ Resolver la regional seleccionada desde "regionales" ya cargadas
                const regionalEncontrada = regionales.find(
                    (r) => r.codRegional === centro.codRegional
                );

                setSelectedRegional(
                    regionalEncontrada
                        ? {
                            value: regionalEncontrada.nombreRegional,
                            label: regionalEncontrada.nombreRegional,
                            codRegional: regionalEncontrada.codRegional,
                            id: regionalEncontrada._id,
                        }
                        : null
                );
            }
        } catch (error) {
            console.error("Error al obtener datos del centro:", error.message);
            Swal.fire("Error", "No se pudo cargar el centro de formación", "error");
        }
    }, [id, regionales]);

    const saveCentro = async () => {
        try {
            setLoading(true);

            const newCentro = {
                codCentro,
                tiposedeCentro,
                nombreanteriorCentro,
                nombreCentro,
                nombreAgrupacionCentro,
                municipioCentro,
                direccionCentro,
                codRegional,
            };

            await axios.post("/api/centrosdeformacion/add", newCentro);

            cleanData();
            Swal.fire({
                icon: "success",
                title: "Centro de Formación registrado con éxito",
                showConfirmButton: false,
                timer: 1500,
            }).then(() => navigate("/institucional/centro/"));

            actualizarCentros?.();
        } catch (error) {
            Swal.fire("Error", error.response?.data?.message || "Error al guardar", "error");
        } finally {
            setLoading(false);
        }
    };

    const updateCentro = async () => {
        try {
            setLoading(true);

            const updatedCentro = {
                codCentro,
                tiposedeCentro,
                nombreanteriorCentro,
                nombreCentro,
                nombreAgrupacionCentro,
                municipioCentro,
                direccionCentro,
                codRegional,
                regionalObjectId: selectedRegional?.id || null,
            };

            await axios.put(`/api/centrosdeformacion/update/${id}`, updatedCentro);

            cleanData();
            Swal.fire({
                icon: "success",
                title: "Centro de formación actualizado con éxito",
                showConfirmButton: false,
                timer: 1500,
            }).then(() => navigate("/institucional/centro/"));

            actualizarCentros?.();
        } catch (error) {
            Swal.fire("Error", error.response?.data?.message || "Error al actualizar", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleRegionalChange = (selectedOption) => {
        setSelectedRegional(selectedOption);
        setCodRegional(selectedOption?.codRegional || "");
    };

    const actions = async (e) => {
        e.preventDefault();
        if (loading) return;

        if (!codCentro || !nombreCentro.trim()) {
            Swal.fire("Atención", "Código y Nombre Centro son obligatorios.", "warning");
            return;
        }

        isEdit ? await updateCentro() : await saveCentro();
    };

    useEffect(() => {
        const cargar = async () => {
            setLoading(true);
            await obtenerRegionales();
            setLoading(false);
        };
        cargar();
    }, [obtenerRegionales]);

    useEffect(() => {
        const cargarEdicion = async () => {
            if (!id) return;
            setLoading(true);
            await getData();
            setLoading(false);
        };
        cargarEdicion();
    }, [id, getData]);

    const selectStyles = useMemo(
        () => ({
            control: (base, state) => ({
                ...base,
                minHeight: 38,
                borderColor: state.isFocused ? "#86b7fe" : "#dee2e6",
                boxShadow: state.isFocused ? "0 0 0 .25rem rgba(13,110,253,.25)" : "none",
                ":hover": { borderColor: state.isFocused ? "#86b7fe" : "#ced4da" },
            }),
            menu: (base) => ({ ...base, zIndex: 9999 }),
        }),
        []
    );

    return (
        <div className="bg-body-tertiary py-4">
            <div className="container-fluid px-2 px-md-4">
                <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
                    {/* Header */}
                    <div className="px-3 px-md-4 py-3 border-bottom bg-white">
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                            <div>
                                <h5 className="mt-2 mb-1 fw-semibold">
                                    {isEdit ? "Editar Centro de Formación" : "Registrar Centro de Formación"}
                                </h5>
                                <small className="text-secondary">
                                    Completa la información del centro y asócialo a una regional.
                                </small>
                            </div>

                            <div className="d-flex gap-2 flex-wrap">
                                <button
                                    className="btn btn-outline-dark"
                                    type="button"
                                    onClick={() => navigate("/institucional/centro/")}
                                    disabled={loading}>
                                    <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                                    Volver
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="p-3 p-md-4">
                        {loading ? (
                            <div className="text-center text-secondary py-5">
                                <div className="spinner-border" role="status" />
                                <div className="mt-3">Cargando datos…</div>
                            </div>
                        ) : (
                            <form onSubmit={actions}>
                                <div className="row g-3">
                                    <div className="col-12 col-md-2">
                                        <label className="form-label fw-semibold">Código *</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-white">
                                                <i className="fa-solid fa-hashtag"></i>
                                            </span>
                                            <input
                                                className="form-control text-uppercase"
                                                value={codCentro}
                                                onChange={(e) => setCodCentro(e.target.value)}
                                                required
                                                maxLength={10}
                                                placeholder="Ej: 12"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-3">
                                        <label className="form-label fw-semibold">Tipo Sede</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-white">
                                                <i className="fa-solid fa-map-pin"></i>
                                            </span>
                                            <input
                                                className="form-control"
                                                value={tiposedeCentro}
                                                onChange={(e) => setTiposedeCentro(e.target.value)}
                                                placeholder="Ej: Principal / Subsede"
                                                maxLength={40}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-3">
                                        <label className="form-label fw-semibold">Nombre Anterior</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-white">
                                                <i className="fa-solid fa-clock-rotate-left"></i>
                                            </span>
                                            <input
                                                className="form-control"
                                                value={nombreanteriorCentro}
                                                onChange={(e) => setNombreanteriorCentro(e.target.value)}
                                                placeholder="(Opcional)"
                                                maxLength={80}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-4">
                                        <label className="form-label fw-semibold">Nombre Centro *</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-white">
                                                <i className="fa-solid fa-building-columns"></i>
                                            </span>
                                            <input
                                                className="form-control"
                                                value={nombreCentro}
                                                onChange={(e) => setNombreCentro(e.target.value)}
                                                placeholder="Ej: Centro de Servicios…"
                                                maxLength={120}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-4">
                                        <label className="form-label fw-semibold">Nombre Agrupación</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-white">
                                                <i className="fa-solid fa-layer-group"></i>
                                            </span>
                                            <input
                                                className="form-control"
                                                value={nombreAgrupacionCentro}
                                                onChange={(e) => setNombreAgrupacionCentro(e.target.value)}
                                                placeholder="(Opcional)"
                                                maxLength={120}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-2">
                                        <label className="form-label fw-semibold">Municipio</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-white">
                                                <i className="fa-solid fa-city"></i>
                                            </span>
                                            <input
                                                className="form-control"
                                                value={municipioCentro}
                                                onChange={(e) => setMunicipioCentro(e.target.value)}
                                                placeholder="Ej: Medellín"
                                                maxLength={60}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-3">
                                        <label className="form-label fw-semibold">Dirección</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-white">
                                                <i className="fa-solid fa-location-dot"></i>
                                            </span>
                                            <input
                                                className="form-control"
                                                value={direccionCentro}
                                                onChange={(e) => setDireccionCentro(e.target.value)}
                                                placeholder="Ej: Calle 10 # 20-30"
                                                maxLength={120}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-3">
                                        <label className="form-label fw-semibold">Regional</label>

                                        <Select
                                            options={regionalOptions}
                                            value={selectedRegional}
                                            onChange={handleRegionalChange}
                                            placeholder="Seleccione una regional"
                                            isClearable
                                            styles={selectStyles}

                                            // ✅ CLAVE para que NO lo recorte el contenedor
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>


                                    {/* Botones */}
                                    <div className="col-12 mt-3">
                                        <div className="d-flex flex-column flex-md-row gap-2 justify-content-end">
                                            <button
                                                type="button"
                                                className="btn btn-outline-dark"
                                                onClick={cleanData}
                                                disabled={loading}
                                                title="Limpiar"
                                            >
                                                <i className="fa-solid fa-eraser me-2"></i>
                                                Limpiar
                                            </button>


                                            <button
                                                type="button"
                                                className="btn btn-outline-danger"
                                                onClick={() => {
                                                    cleanData();
                                                    navigate("/institucional/centro/");
                                                }}
                                                disabled={loading}
                                            >
                                                <i className="fa-solid fa-xmark me-2"></i>
                                                Cancelar
                                            </button>

                                            <button
                                                type="submit"
                                                className="btn btn-dark"
                                                disabled={loading}
                                            >
                                                <i
                                                    className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                                                        } me-2`}
                                                ></i>
                                                {loading
                                                    ? isEdit
                                                        ? "Actualizando…"
                                                        : "Guardando…"
                                                    : isEdit
                                                        ? "Actualizar"
                                                        : "Guardar"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegistrarCentros;
